import { useState } from 'react';

import { ListLayout, Modal, ModalSelect, OptionList, SearchInput, Spinner } from '@ingrid/luterkort-react';
import { ShippingLogo } from '@ingrid/react-shipping-logo';

import { SelectModalProps } from '~src/components/Modal/Modal';
import { useTranslation } from '~src/i18n/use-translation';
import { useDebounce } from '~src/lib/hooks/useDebounce';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';
import { useShipmentsListQuery } from '~src/pages/shipment/queries';

type Props = {} & Pick<SelectModalProps, 'trigger' | 'onValueChange'>;

export function ShipmentSelectModal({ trigger, onValueChange }: Props) {
  const [search, setSearch] = useState<string>('');

  const { data = [], isLoading } = useShipmentsListQuery(
    { filtering: { id: useDebounce(search, 300), direction_types: ['OUTBOUND'] } },
    search.length > 3
  );

  const tracking = useTracking();
  const { t } = useTranslation();

  function handleOpenChange(value: boolean) {
    tracking.capture(
      value ? TrackingEventEnum.ConfiguratorPrefillShipmentOpen : TrackingEventEnum.ConfiguratorPrefillShipmentClose
    );
  }

  return (
    <ModalSelect.Root
      onSearchValueChange={setSearch}
      onOpenChange={handleOpenChange}
      onValueChange={onValueChange}
      filterFactory={false}
      searchValue={search}
      shouldCloseOnSelect
      autoFocus
    >
      <Modal.Root>
        <Modal.Trigger>{trigger}</Modal.Trigger>

        <Modal.Content style={{ maxHeight: '400px' }}>
          <Modal.Header title={t('bookingRules.edit.pre-fill.title')} />

          <ModalSelect.Content>
            <ModalSelect.Control>
              <SearchInput placeholder={t('bookingRules.edit.searchShipmentPlaceholder')} />
            </ModalSelect.Control>

            {isLoading ? (
              <Spinner />
            ) : (
              <OptionList.Viewport>
                {data.map(shipment => (
                  <OptionList.Item key={shipment.id} value={shipment.id!}>
                    <ListLayout.Row>
                      <ShippingLogo width={40} method={shipment.shipping_method} />
                      <ListLayout.Stack>
                        <ListLayout.PrimaryText>
                          {t('bookingRules.edit.pre-fill.shipment.address', {
                            from: shipment.address_from?.name,
                            to: shipment.customer_info?.address?.name,
                            fromCountry: shipment.address_from?.country,
                            toCountry: shipment.customer_info?.address?.country,
                          })}
                        </ListLayout.PrimaryText>
                        <ListLayout.SecondaryText>
                          {t('bookingRules.edit.pre-fill.shipment.shipUsing', {
                            method: shipment.shipping_method,
                          })}
                        </ListLayout.SecondaryText>
                      </ListLayout.Stack>
                    </ListLayout.Row>
                  </OptionList.Item>
                ))}
              </OptionList.Viewport>
            )}
          </ModalSelect.Content>

          <Modal.Footer size="sm">
            <ModalSelect.Keyboard />
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </ModalSelect.Root>
  );
}
